/* global suppliersCreateUrl */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';

import { Modal } from 'flowbite';

import ToggleSuppliersSync from '../integrations/activation/ToggleSuppliersSync';
import SupplierContractTermCreateModal from '../supplier_contracts/terms/SupplierContractTermCreateModal';

import SuppliersEditModal from './SuppliersEditModal';
import SuppliersIndexTableRow from './SuppliersIndexTableRow';

import { modalOptions } from 'Helpers/modalOptions';

import Button from 'CommonComponents/Button';
import ConfirmationModal from 'CommonComponents/ConfirmationModal';
import PaginationInfo from 'CommonComponents/PaginationInfo';
import PerPageSelect from 'CommonComponents/PerPageSelect';
import TablePagination from 'CommonComponents/TablePagination';
import TableRowSkeleton from 'CommonComponents/TableRowSkeleton';
import TableSearchInput from 'CommonComponents/TableSearchInput';

import useApiRequest from 'Hooks/useApiRequest';
import useModal from 'Hooks/useModal';
import usePagination from 'Hooks/usePagination';
import { useUsers } from 'Hooks/useUsers';

import CirclePlusIcon from 'Assets/icons/circle-plus.svg';
import FileExportIcon from 'Assets/icons/file-export.svg';

const UPSERT_TERMS_MODAL_ID = 'add-supplier-contract-terms-modal__suppliers';
const DELETE_TERMS_MODAL_ID = 'delete-supplier-contract-term-modal__suppliers';
const DELETE_TERM_ATTACHMENT_MODAL_ID = 'delete-supplier-contract-term-attachment-modal__suppliers';

export default function SuppliersIndexTable(props) {
    const [updatedSupplier, setUpdatedSupplier] = useState({});
    const [deletedSupplier, setDeletedSupplier] = useState({});
    const [isTermsLoading, setIsTermsLoading] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [currentContract, setCurrentContract] = useState(null);
    const [supplierContractTerms, setSupplierContractTerms] = useState(null);
    const [supplierContractTermAttachment, setSupplierContractTermAttachment] = useState(null);
    const [suppliersEditModalElement, setSuppliersEditModalElement] = useState(null);
    const [suppliersDeleteModalElement, setSuppliersDeleteModalElement] = useState(null);
    const [supplierContractTermAttachmentDeleteModalElement, setSupplierContractTermAttachmentDeleteModalElement] = useState(null);
    const [supplierContractTermDeleteModalElement, setSupplierContractTermDeleteModalElement] = useState(null);
    const [deleteSupplierFailedMsg, setDeleteSupplierFailedMsg] = useState();

    const [actionCompleted, setActionCompleted] = useState(false);
    const [actionInProgress, setActionInProgress] = useState(false);
    const [actionFailed, setActionFailed] = useState(false);

    const suppliersEditModalRef = useRef();
    const suppliersDeleteModalRef = useRef();
    const supplierContractTermAttachmentDeleteModalRef = useRef();
    const supplierContractTermDeleteModalRef = useRef();

    const supplierContractTermCreateModal = useModal();

    const { users } = useUsers();

    const pagination = usePagination({ perPage: 25 });

    const fetchSuppliersRequest = useApiRequest({ url: props.suppliersIndexUrl });

    const suppliers = useMemo(() => {
        if (!fetchSuppliersRequest.data?.suppliers) {
            return [];
        }

        return fetchSuppliersRequest.data.suppliers.data.map((supplier) => {
            supplier.totalContracts = supplier.contracts.reduce((acc, contract) => acc + (Number(contract.cashflow.total) || 0), 0);

            return supplier;
        });
    }, [fetchSuppliersRequest.data]);

    const canBeEdited = useMemo(() => {
        const { integrationConfigured, syncEnabled } = fetchSuppliersRequest.data || {};
        return !integrationConfigured || (integrationConfigured && !syncEnabled);
    }, [fetchSuppliersRequest.data]);

    useEffect(() => {
        suppliersEditModalRef && setSuppliersEditModalElement(new Modal(suppliersEditModalRef.current, modalOptions(false)));
        suppliersDeleteModalRef && setSuppliersDeleteModalElement(new Modal(suppliersDeleteModalRef.current, modalOptions(false)));
        supplierContractTermDeleteModalRef &&
            setSupplierContractTermDeleteModalElement(new Modal(supplierContractTermDeleteModalRef.current, modalOptions(false)));
        supplierContractTermAttachmentDeleteModalRef &&
            setSupplierContractTermAttachmentDeleteModalElement(new Modal(supplierContractTermAttachmentDeleteModalRef.current, modalOptions(false)));
    }, []);

    useEffect(() => {
        if (fetchSuppliersRequest.data?.suppliers) {
            const { meta } = fetchSuppliersRequest.data.suppliers;
            pagination.setTotal(meta.total);
            pagination.setLastPage(meta.last_page);
            pagination.setFrom(meta.from);
            pagination.setTo(meta.to);
        }
    }, [fetchSuppliersRequest.data]);

    useEffect(() => {
        fetchSuppliers();
    }, [pagination.page, pagination.perPage, searchValue]);

    useEffect(() => {
        if (Object.keys(updatedSupplier).length > 0) {
            suppliersEditModalElement && suppliersEditModalElement.show();
        }
    }, [updatedSupplier]);

    useEffect(() => {
        if (Object.keys(deletedSupplier).length > 0) {
            suppliersDeleteModalElement && suppliersDeleteModalElement.show();
        }
    }, [deletedSupplier]);

    function clickRow(supplier) {
        setUpdatedSupplier(supplier);
    }

    function deleteRow(supplier) {
        setDeletedSupplier(supplier);
    }

    const successHandler = useCallback(() => {
        setUpdatedSupplier({});
        setDeletedSupplier({});
        fetchSuppliers();
        suppliersEditModalElement && suppliersEditModalElement.hide();
        suppliersDeleteModalElement && suppliersDeleteModalElement.hide();
    }, [updatedSupplier, deletedSupplier]);

    const deleteAttachment = (attachment) => {
        setSupplierContractTermAttachment(attachment);
        supplierContractTermAttachmentDeleteModalElement && supplierContractTermAttachmentDeleteModalElement.show();
    };

    const downloadAttachment = (e, attachment) => {
        e.preventDefault();
        const url = props.downloadSupplierContractsTermAttachmentUrl
            .replace('CONTRACT_ID', currentContract?.id)
            .replace('TERM_ID', attachment.term_id)
            .replace('ATTACHMENT_ID', attachment.id);

        axios({
            url: url,
            method: 'GET',
            responseType: 'blob',
        }).then((response) => {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', attachment.file_name);
            document.body.appendChild(link);
            link.click();
        });
    };

    const fetchSupplierContractTerms = (contractId) => {
        const url = props.supplierContractTermsIndexUrl.replace('CONTRACT_ID', contractId);
        setIsTermsLoading(true);

        axios
            .get(url)
            .then(({ data }) => {
                setSupplierContractTerms(data);
            })
            .catch((error) => console.error(error))
            .finally(() => setIsTermsLoading(false));
    };

    const fetchSuppliers = () => {
        fetchSuppliersRequest.get({ page: pagination.page, per_page: pagination.perPage, search: searchValue });
    };

    const handleChange = (event) => {
        setSearchValue(event.target.value);
        pagination.setPage(1);
    };

    const onAddTerms = (contract) => {
        setSupplierContractTerms(null);
        setCurrentContract(contract);
        supplierContractTermCreateModal.show();
    };

    const onViewTerms = (contract) => {
        window.location.href = props.supplierContractTermsIndexUrl.replace('CONTRACT_ID', contract.id);
    };

    const onEditTerms = (contract) => {
        // Drop the previous contract's terms first: a failed fetch leaves them in place otherwise,
        // and the modal would open on this contract showing the last one's values.
        setSupplierContractTerms(null);
        setCurrentContract(contract);
        fetchSupplierContractTerms(contract.id);
        supplierContractTermCreateModal.show();
    };

    const onDeleteTerms = (contract) => {
        setCurrentContract(contract);
        fetchSupplierContractTerms(contract.id);
        supplierContractTermDeleteModalElement && supplierContractTermDeleteModalElement.show();
    };

    const cleanConfirmationModalStates = useCallback(() => {
        setActionInProgress(false);
        setActionFailed(false);
        setActionCompleted(false);
    }, [actionInProgress, actionFailed, actionCompleted]);

    const triggerDeleteAttachment = useCallback(() => {
        setActionInProgress(true);

        const url = props.deleteSupplierContractsTermAttachmentUrl
            .replace('SUPPLIER_CONTRACT_ID', supplierContractTerms?.supplier_contract_id)
            .replace('TERM_ID', supplierContractTerms?.id)
            .replace('ATTACHMENT_ID', supplierContractTermAttachment.id);

        axios
            .delete(url)
            .then(() => {
                supplierContractTermAttachmentDeleteModalElement && supplierContractTermAttachmentDeleteModalElement.hide();
                fetchSupplierContractTerms(currentContract?.id);
            })
            .catch(() => {
                setActionInProgress(false);
                setActionFailed(true);
            })
            .finally(() => {
                setActionInProgress(false);
                setActionCompleted(true);
            });
    }, [supplierContractTerms, supplierContractTermAttachment]);

    const triggerDeleteSupplier = useCallback(() => {
        if (Object.keys(deletedSupplier).length > 0) {
            setActionInProgress(true);
            axios
                .delete(props.suppliersDeleteUrl.replace('SUPPLIER_ID', deletedSupplier.id), { data: {} })
                .then((response) => {
                    setActionInProgress(false);
                    setActionCompleted(true);
                })
                .catch((e) => {
                    setDeleteSupplierFailedMsg($t('{{supplier_name}} could not be deleted', { supplier_name: deletedSupplier?.name }));
                    if (e?.response?.status === 403 && e?.response?.data?.message) {
                        setDeleteSupplierFailedMsg(e.response.data.message);
                    }

                    setActionInProgress(false);
                    setActionFailed(true);
                });
        }
    }, [deletedSupplier]);

    const triggerDeleteTerm = useCallback(() => {
        setActionInProgress(true);

        const url = props.supplierContractTermDeleteUrl
            .replace('SUPPLIER_CONTRACT_ID', supplierContractTerms?.supplier_contract_id)
            .replace('TERM_ID', supplierContractTerms?.id);

        axios
            .delete(url)
            .then(() => {
                supplierContractTermDeleteModalElement && supplierContractTermDeleteModalElement.hide();
                successHandler();
            })
            .catch(() => setActionFailed(true))
            .finally(() => setActionInProgress(false));
    }, [supplierContractTerms, currentContract]);

    return (
        <>
            <div className="table__card">
                <div className="tw-flex tw-items-end tw-justify-between tw-mb-6">
                    <div className="tw-flex tw-flex-col tw-gap-y-1">
                        <h4 className="table__header">{$t('Suppliers')}</h4>
                        <div className="table__pagination-info">
                            <PaginationInfo to={pagination.to} from={pagination.from} total={pagination.total} />
                            <TablePagination lastPage={pagination.lastPage} pageChange={(data) => pagination.setPage(+data.selected + 1)} />
                        </div>
                    </div>
                    <div className="tw-flex tw-items-center tw-space-x-2">
                        <div>
                            <PerPageSelect
                                value={pagination.perPage}
                                onChange={(e) => {
                                    pagination.setPerPage(parseInt(e.target.value));
                                }}
                            />
                        </div>
                        <div>
                            <TableSearchInput searchValue={searchValue} handleChange={handleChange} />
                        </div>
                    </div>
                </div>
                <div id={'suppliers-table'} className="inbud-table_wrapper">
                    <table className="inbud-table tw-table-auto">
                        <thead>
                            <tr>
                                <th>{$t('Supplier Name')}</th>
                                <th>{$t('Supplier Code')}</th>
                                <th>{$t('Contact Name')}</th>
                                <th>{$t('Contact Email')}</th>
                                <th>{$t('Location')}</th>
                                <th>{$t('Post Code')}</th>
                                <th>{$t('Total Value')}</th>
                                <th>{$t('Actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {!fetchSuppliersRequest.loading &&
                                suppliers.length > 0 &&
                                suppliers.map((supplier) => {
                                    return (
                                        <SuppliersIndexTableRow
                                            key={supplier.id}
                                            supplier={supplier}
                                            clickRow={clickRow}
                                            deleteRow={deleteRow}
                                            onAddTerms={onAddTerms}
                                            onViewTerms={onViewTerms}
                                            onEditTerms={onEditTerms}
                                            onDeleteTerms={onDeleteTerms}
                                            canBeEdited={canBeEdited}
                                        />
                                    );
                                })}
                            {fetchSuppliersRequest.loading && <TableRowSkeleton rows={10} columns={8} />}
                            {!fetchSuppliersRequest.loading && suppliers.length === 0 && (
                                <tr>
                                    <td colSpan="100">
                                        <a href={props.suppliersCreateUrl}>{$t('No Suppliers found click here to Add New Supplier')}</a>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                <div className="tw-flex tw-justify-end tw-gap-x-2 tw-mt-4">
                    <Button variant="primary" as="a" href={props.exportToFile} data-action="navigate">
                        <FileExportIcon className="tw-w-3 tw-h-3" />
                        <div>Export to file</div>
                    </Button>
                    {canBeEdited && (
                        <Button variant="primary" as="a" href={suppliersCreateUrl}>
                            <CirclePlusIcon className="tw-w-3 tw-h-3" />
                            <div>{$t('Add new Supplier')}</div>
                        </Button>
                    )}
                    <ToggleSuppliersSync
                        integrationConfigured={fetchSuppliersRequest.data?.integrationConfigured}
                        syncEnabled={fetchSuppliersRequest.data?.syncEnabled}
                        action={props.action}
                        toggleUrl={props.toggleUrl}
                        model={props.model}
                        syncNowUrl={props.syncNowUrl}
                        toggleCallback={() => {
                            fetchSuppliers();
                        }}
                    />
                </div>
            </div>

            <SuppliersEditModal
                closeHandler={successHandler}
                successHandler={successHandler}
                modalId={Object.keys(updatedSupplier).length > 0 ? updatedSupplier.id : 'suppliers-edit-modal'}
                modalRef={suppliersEditModalRef}
                modalElement={suppliersEditModalElement}
                supplier={updatedSupplier}
                suppliersUpdateUrl={props.suppliersUpdateUrl}
                suppliersCreateUrl={props.suppliersCreateUrl}
            />

            <ConfirmationModal
                closeHandler={successHandler}
                successHandler={successHandler}
                triggerAction={triggerDeleteSupplier}
                cleanStates={cleanConfirmationModalStates}
                allowAction={Object.keys(deletedSupplier).length > 0}
                modalId={Object.keys(deletedSupplier).length > 0 ? deletedSupplier.id : 'suppliers-delete-modal'}
                modalRef={suppliersDeleteModalRef}
                modalElement={suppliersDeleteModalElement}
                request={actionInProgress}
                actionCompleted={actionCompleted}
                actionFailed={actionFailed}
                confirmationMsg={$t('Are you sure you want to delete {{name}}?', { name: deletedSupplier?.name })}
                actionCompletedMsg={$t('{{supplier_name}} deleted', { supplier_name: deletedSupplier?.name })}
                actionFailedMsg={deleteSupplierFailedMsg}
                title={$t('Delete Supplier')}
                cancelButtonText="Cancel"
                primaryButtonText="Delete"
            />

            <SupplierContractTermCreateModal
                modal={supplierContractTermCreateModal}
                isLoading={isTermsLoading}
                storeUrl={props.supplierContractTermStoreUrl}
                updateUrl={props.supplierContractTermUpdateUrl}
                contract={currentContract}
                terms={supplierContractTerms}
                users={users}
                onCreated={successHandler}
                onDeleteAttachment={deleteAttachment}
                onDownloadAttachment={downloadAttachment}
                eventStartDate={props.eventStartDate}
                eventEndDate={props.eventEndDate}
            />

            <ConfirmationModal
                triggerAction={triggerDeleteAttachment}
                cleanStates={cleanConfirmationModalStates}
                allowAction={supplierContractTermAttachment != null}
                modalId={DELETE_TERM_ATTACHMENT_MODAL_ID}
                modalRef={supplierContractTermAttachmentDeleteModalRef}
                modalElement={supplierContractTermAttachmentDeleteModalElement}
                request={actionInProgress}
                actionCompleted={actionCompleted}
                actionFailed={actionFailed}
                confirmationMsg={$t('Are you sure you want to delete {{name}}?', { name: supplierContractTermAttachment?.file_name })}
                actionCompletedMsg=""
                actionFailedMsg={$t('{{name}} could not be deleted', { name: supplierContractTermAttachment?.file_name })}
                title={$t('Delete attachment')}
                cancelButtonText="Cancel"
                primaryButtonText="Delete"
            />

            <ConfirmationModal
                triggerAction={triggerDeleteTerm}
                cleanStates={cleanConfirmationModalStates}
                allowAction={currentContract != null && supplierContractTerms != null}
                modalId={DELETE_TERMS_MODAL_ID}
                modalRef={supplierContractTermDeleteModalRef}
                modalElement={supplierContractTermDeleteModalElement}
                request={actionInProgress}
                actionCompleted={actionCompleted}
                actionFailed={actionFailed}
                confirmationMsg={$t('Are you sure you want to delete terms of contract {{ref}}?', { ref: currentContract?.contract_reference })}
                actionCompletedMsg=""
                actionFailedMsg={$t('contract {{ref}} terms could not be deleted', { ref: currentContract?.contract_reference })}
                title={$t('Delete contract terms')}
                cancelButtonText="Cancel"
                primaryButtonText="Delete"
            />
        </>
    );
}
